import React, { useEffect } from "react";
import Nav from "../components/Nav/Nav";
import { LoadingOutlined } from "@ant-design/icons";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import "../App.css";
import "../style/OrderTracking.css";
import { downloadDigitalProduct } from "../api/backend";

function OrderTracking() {
  const { id } = useParams();
  const navigate = useNavigate();

  const {
    currentOrder,
    getOrder,
    isLoading,
  } = useAuth();

  useEffect(() => {
    const unsubscribe = getOrder(id);

    return () => {
      if (typeof unsubscribe === "function") unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const formatAmount = (value) =>
    `${(Number(value || 0) / 100).toFixed(2)} €`;

  const formatDate = (createdAt) => {
    if (!createdAt) return "";

    const date = createdAt?.seconds
      ? new Date(createdAt.seconds * 1000)
      : new Date(createdAt);

    return date.toLocaleDateString("fr-FR", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  const getStatusTitle = (status) => {
    if (status === "pending_payment") return "Validation du paiement";
    if (status === "paid") return "Commande validée";
    if (status === "preparing") return "Commande en préparation";
    if (status === "ready") return "Commande prête";
    if (status === "out_for_delivery") return "Commande en livraison";
    if (status === "delivered") return "Commande livrée";
    if (status === "completed") return "Commande terminée";
    if (status === "cancelled") return "Commande annulée";
    if (status === "payment_failed") return "Paiement échoué";
    return "Commande";
  };

  const getServiceLabel = (serviceType) => {
    if (serviceType === "table") return "À table";
    if (serviceType === "takeaway") return "Click & Collect";
    if (serviceType === "delivery") return "Livraison à domicile";
    if (serviceType === "digital") return "Digital";
    return "";
  };

  const getStatusText = (status) => {
    if (status === "pending_payment") return "en cours de validation";
    if (status === "paid") return "validée";
    if (status === "preparing") return "en préparation";
    if (status === "ready") return "prête";
    if (status === "out_for_delivery") return "en livraison";
    if (status === "delivered") return "livrée";
    if (status === "completed") return "terminée";
    if (status === "cancelled") return "annulée";
    if (status === "payment_failed") return "en échec de paiement";
    return "validée";
  };

  const formatTime = (dateValue) => {
    if (!dateValue) return "";

    const date = dateValue?.seconds
      ? new Date(dateValue.seconds * 1000)
      : new Date(dateValue);

    return date.toLocaleTimeString("fr-FR", {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  
  const getDigitalDownloadInfo = (item) => {
    return {
      productId: item?.productId || "",
      name: item?.digitalFileName || "Télécharger",
      isDownloadable: !!item?.digitalFilePath,
    };
  };
  
  const handleDownload = async (productId) => {
    if (!currentOrder?.id || !productId) return;
  
    const downloadWindow = window.open("", "_blank");
  
    try {
      const data = await downloadDigitalProduct({
        orderId: currentOrder.id,
        productId,
      });
  
      if (data?.url) {
        if (downloadWindow) {
          downloadWindow.location.href = data.url;
        } else {
          window.location.href = data.url;
        }
      } else {
        if (downloadWindow) downloadWindow.close();
        alert("Lien de téléchargement introuvable.");
      }
    } catch (error) {
      console.log(error);
      if (downloadWindow) downloadWindow.close();
      alert("Impossible de télécharger le fichier.");
    }
  };
  

  return (
    <div className="menu-wrapper">
      <div className="menu-container2">
        <div className="one-page">
          <Nav menu />

          <div className="main">
            <div className="order-tracking-page">
              {isLoading ? (
                <div className="orders-loading">
                  <LoadingOutlined spin />
                </div>
              ) : !currentOrder ? (
                <div className="orders-empty">
                  <p>Commande introuvable</p>
                </div>
              ) : (
                <>
                  <div className="order-tracking-section order-tracking-top">
                    <div className="title-back">
                      <h1 className="order-tracking-title">
                        {getStatusTitle(currentOrder.status)}
                      </h1>
                    </div>

                    <p className="order-tracking-intro">
                      Votre commande n°
                      {currentOrder.id?.slice(-6)?.toUpperCase()} du{" "}
                      {formatDate(currentOrder.createdAt)} à{" "}
                      {formatTime(currentOrder.paidAt || currentOrder.createdAt)}{" "}
                      chez {currentOrder.restaurantName || "Restaurant"}{" "}
                      {getServiceLabel(currentOrder.serviceType)
                        ? `(${getServiceLabel(currentOrder.serviceType)})`
                        : ""}
                      {" "}est {getStatusText(currentOrder.status)}.
                      {currentOrder.status === "cancelled" && (
                        <>{" "}Vous avez été intégralement remboursé. Le remboursement apparaît sur votre compte sous 5 à 10 jours.</>
                      )}
                      {currentOrder.serviceType === "digital" && currentOrder.customerEmail && currentOrder.status !== "cancelled" && (
                        <>{" "}Un email avec vos liens de téléchargement a été envoyé à {currentOrder.customerEmail}.</>
                      )}
                    </p>
                  </div>

                  <div className="order-tracking-section">
                    <div className="order-tracking-summary">
                      <p className="order-tracking-order-number">
                        Commande n°{currentOrder.id?.slice(-6)?.toUpperCase()}
                      </p>

                      <p
                        className="order-tracking-restaurant"
                        onClick={() =>
                          currentOrder?.restaurantId &&
                          navigate(`/shop/${currentOrder.restaurantId}/menu`)
                        }
                      >
                        {currentOrder.restaurantName}
                      </p>

                      <p className="order-tracking-service">
                        {getServiceLabel(currentOrder.serviceType)}
                        {currentOrder.serviceType === "table" && currentOrder.tableNumber
                          ? ` · Table ${currentOrder.tableNumber}`
                          : ""}
                      </p>

                      {currentOrder.serviceType === "delivery" &&
                      currentOrder.deliveryAddress ? (
                        <p className="order-tracking-service">
                          {currentOrder.deliveryAddress}
                        </p>
                      ) : null}
                    </div>

                    <div className="order-tracking-divider" />

                    <div className="order-tracking-items">
                      {currentOrder.items?.map((item, index) => {
                        const download = getDigitalDownloadInfo(item);

                        return (
                          <div key={index} className="order-tracking-item">
                            <div className="order-tracking-item-left">
                              <p className="order-tracking-item-name">
                                {item.quantity}× {item.productName}
                              </p>

                              {item.optionName && (
                                <p className="order-tracking-item-sub">
                                  {item.optionName}
                                </p>
                              )}

                              {item.supplementNames?.length > 0 && (
                                <div className="order-tracking-item-sub">
                                  {item.supplementNames.map((supplement, i) => (
                                    <div key={i}>{supplement}</div>
                                  ))}
                                </div>
                              )}

                              {download.isDownloadable && (
                                <div style={{ marginTop: 8 }}>
                                 

                                  <p
                                    onClick={() => handleDownload(download.productId)}
                                    style={{
                                      margin: 0,
                                      cursor: "pointer",
                                      textDecoration: "underline",
                                    }}
                                  >
                                    Télécharger
                                  </p>
                                </div>
                              )}
                            </div>

                            <div className="order-tracking-item-price">
                              {formatAmount(item.lineTotal)}
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    <div className="order-tracking-divider" />

                    {Number(currentOrder.deliveryFee || 0) > 0 && (
                      <>
                        <div className="order-tracking-total-row light">
                          <p>Frais de livraison</p>
                          <p>{formatAmount(currentOrder.deliveryFee)}</p>
                        </div>
                        <div className="order-tracking-divider" />
                      </>
                    )}

                    {Number(currentOrder.tipAmount || 0) > 0 && (
                      <>
                        <div className="order-tracking-total-row light">
                          <p>Pourboire</p>
                          <p>{formatAmount(currentOrder.tipAmount)}</p>
                        </div>
                        <div className="order-tracking-divider" />
                      </>
                    )}

                    <div className="order-tracking-total-row total">
                      <p>Total</p>
                      <p>
                        {formatAmount(currentOrder.totalAmount || currentOrder.amount)}
                      </p>
                    </div>

                    {currentOrder.status === "cancelled" && (
                      <div className="order-tracking-total-row">
                        <p>Remboursé</p>
                        <p>
                          {formatAmount(currentOrder.refundedAmount || currentOrder.totalAmount || currentOrder.amount)}
                        </p>
                      </div>
                    )}

                    <div className="order-tracking-divider" />

                    <div className="order-tracking-payment">
                      <div style={{ display: "none" }}>
                        <p className="order-tracking-payment-title">
                          Moyen de paiement
                        </p>
                        <p className="order-tracking-payment-method">
                          Carte bancaire
                        </p>
                      </div>

                      {(currentOrder?.restaurantContact?.phone ||
                        currentOrder?.restaurantContact?.email ||
                        currentOrder?.restaurantContact?.whatsapp) && (
                        <div className="order-tracking-payment-title">
                          <p className="order-tracking-contact">Service client</p>

                          {currentOrder?.restaurantContact?.phone && (
                            <p className="order-tracking-payment-method">
                              {currentOrder.restaurantContact.phone}
                            </p>
                          )}

                          {currentOrder?.restaurantContact?.email && (
                            <p className="order-tracking-payment-method">
                              {currentOrder.restaurantContact.email}
                            </p>
                          )}

                          {currentOrder?.restaurantContact?.whatsapp && (
                            <p
                              className="order-tracking-payment-method"
                              style={{
                                textDecoration: "underline",
                                cursor: "pointer",
                              }}
                              onClick={() =>
                                window.open(
                                  `https://wa.me/${currentOrder.restaurantContact.whatsapp.replace(/\D/g, "")}`,
                                  "_blank"
                                )
                              }
                            >
                              WhatsApp
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default OrderTracking;